import { useEffect, useMemo, useRef, useState } from 'react';
import { buildAccuracyAskSteps, pickAccuracyAskMessage } from '../utils/accuracyAskLoader';

const MESSAGE_ROTATE_MS = 1400;

/**
 * Cycles through scope-aware loader steps while `active` is true.
 * Resets when a new run starts (active false → true).
 */
export function useAccuracyAskLoaderSimulation({ active, scope = {} }) {
    const stepPlan = useMemo(
        () => buildAccuracyAskSteps(scope),
        [scope.includeVdr, scope.includeIrl, scope.includeObservations],
    );

    const [stageIndex, setStageIndex] = useState(0);
    const [messageTick, setMessageTick] = useState(0);
    const runIdRef = useRef(0);

    useEffect(() => {
        if (!active) {
            setStageIndex(0);
            setMessageTick(0);
            return undefined;
        }

        const runId = ++runIdRef.current;
        setStageIndex(0);
        setMessageTick(0);

        let idx = 0;
        let timeoutId;

        const advance = () => {
            if (runIdRef.current !== runId || !active) return;

            const stage = stepPlan[idx];
            if (!stage) return;

            setStageIndex(idx);

            timeoutId = window.setTimeout(() => {
                if (runIdRef.current !== runId) return;
                idx += 1;
                if (idx < stepPlan.length) {
                    advance();
                } else {
                    setStageIndex(stepPlan.length - 1);
                }
            }, stage.durationMs);
        };

        advance();

        const msgInterval = window.setInterval(() => {
            setMessageTick(t => t + 1);
        }, MESSAGE_ROTATE_MS);

        return () => {
            window.clearTimeout(timeoutId);
            window.clearInterval(msgInterval);
        };
    }, [active, stepPlan]);

    const currentStage = stepPlan[Math.min(stageIndex, stepPlan.length - 1)] || stepPlan[0];
    const activeMessage = pickAccuracyAskMessage(currentStage, messageTick);

    const stepsWithStatus = stepPlan.map((step, i) => ({
        ...step,
        status: i < stageIndex ? 'done' : i === stageIndex ? 'active' : 'pending',
    }));

    return {
        stageIndex,
        stepIndex: stageIndex,
        currentStage,
        currentStep: currentStage,
        activeMessage,
        stepPlan,
        stepsWithStatus,
        totalSteps: stepPlan.length,
    };
}

export default useAccuracyAskLoaderSimulation;
